"use client";

import { useEffect, useState } from "react";
import { Coins, Headset, HelpCircle } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { AeraButton } from "@/app/components/ui/AeraButton";

interface SearchInputProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  placeholder?: string;
  className: string;
  fieldClassName?: string;
  onHowItWorksClick?: () => void;
}

export default function SearchInput({
  searchQuery,
  setSearchQuery,
  // placeholder = "Search services...",
  placeholder = "ค้นหาบริการ...",
  className,
  fieldClassName,
  onHowItWorksClick,
}: SearchInputProps) {
  const { isLoggedIn } = useAuthState();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Avoid hydration mismatch: SSR/initial client render should match,
  // then we reveal logged-in UI after mount.
  const showLoggedInUi = mounted && isLoggedIn;

  return (
    <div
      className={`${className} items-center ${showLoggedInUi ? "justify-between" : "justify-end"} ps-2 pe-2 py-2 bg-aera-offwhite gap-2`}
    >
      <div
        className={`${showLoggedInUi ? "block" : "hidden"} relative transition-all duration-300 overflow-hidden opacity-100 w-full ${fieldClassName ?? "max-w-96"} border border-aera-lightgray/80 rounded-xl`}
      >
        <div className="absolute left-3 top-2 sm:top-2.5 text-aera-gray">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-aera-white text-aera-charcoal placeholder:text-aera-gray pl-9 pr-4 py-2 sm:py-2.5 text-xs border-0 outline-none transition-all shadow-sm"
        />
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {showLoggedInUi && (
          <>
            <AeraButton
              href="/credits/topup"
              variant="outline"
              textSize="xs"
              fontWeight="normal"
              paddingSize="xs"
              iconRight={
                <Coins className="w-4 h-4 text-aera-amber" strokeWidth={2} />
              }
              aria-label="เติมเครดิต"
            >
              <span className="hidden lg:inline pt-0.5">เครดิต</span>
            </AeraButton>
          </>
        )}

        <AeraButton
          href="/support"
          variant="warm"
          textSize="xs"
          fontWeight="normal"
          paddingSize="xs"
          iconRight={
            <Headset
              className="w-4 h-4 text-aera-amber"
              strokeWidth={2}
              aria-hidden
            />
          }
          className="whitespace-nowrap"
          aria-label="ช่วยเหลือ"
        >
          <span className={`${isLoggedIn ? "hidden lg:inline" : ""} pt-0.5`}>
            ช่วยเหลือ
          </span>
        </AeraButton>

        {onHowItWorksClick && (
          <AeraButton
            variant="secondary"
            textSize="xs"
            fontWeight="normal"
            paddingSize="xs"
            onClick={onHowItWorksClick}
            iconRight={<HelpCircle className="w-4 h-4" strokeWidth={2} />}
          >
            <span className={`${isLoggedIn ? "hidden lg:inline" : ""} pt-0.5`}>
              วิธีการทำงาน
            </span>
          </AeraButton>
        )}
      </div>
    </div>
  );
}
